/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Button, EmptyStateDashboardNoPermissionIllustration, SimpleMenu, useMenuState } from "@octopusdeploy/design-system-components";
import type { PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { useAggregateAPIOperationStatus, useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ProjectResource, Repository, DashboardConfigurationResource, LicenseUsageResource, LicenseStatusResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { isEqual } from "lodash";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { AnalyticActionDispatcher } from "~/analytics/Analytics";
import { useAnalyticSimpleActionDispatch, Action, useAnalyticActionDispatch } from "~/analytics/Analytics";
import { AddProjectGroup } from "~/areas/projects/components/Projects/AddProjectGroup";
import type { ProjectsFilter } from "~/areas/projects/components/Projects/ProjectsDashboard";
import { defaultProjectsPageSize, isProjectsPageSize, ProjectsDashboardPageContent } from "~/areas/projects/components/Projects/ProjectsDashboard";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import Dialog from "~/components/Dialog/Dialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { Level1EmptyStateLayout } from "~/components/GettingStarted/Level1EmptyStateLayout";
import { PageLoading } from "~/components/PageContent/PageLoading";
import PermissionCheck, { hasPermission, isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import DashboardOnboardingLayout from "~/components/ProjectBasedActivation/DashboardOnboardingLayout";
import NewProjectWizard from "~/components/ProjectBasedActivation/NewProjectWizard";
import { UpsellDialogWithLicenseUsage } from "~/components/UpsellDialog/UpsellDialogWithLicenseUsage";
import { EntitlementLimitPopUpEvent } from "~/components/UpsellDialog/upsellDialogAnalytics";
import { getLicenseLimit } from "~/hooks/useLicenseLimit";
import useLocalStorage from "~/hooks/useLocalStorage";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { getImportExportMenuItems } from "../ImportExport/ImportExportMenu";
import { projectStatusActions } from "../ProjectStatus/actions";
import type { ProjectStatus } from "../ProjectStatus/useProjectStatus";
export interface ProjectsQueryParams {
    page?: number;
    pageSize?: number;
    searchValue: string;
    includeProjectGroupId?: string;
}
interface ProjectsPageProps {
    spaceId: string;
    queryParams: ProjectsQueryParams;
    setQueryParams: QueryParamValuesSetter<ProjectsQueryParams>;
    projectStatus: ProjectStatus;
    loaderData: ProjectsPageLoaderData;
}
const DashboardDispatcher = ({ dispatchAction }: {
    dispatchAction: AnalyticActionDispatcher;
}) => {
    const [dispatched, setDispatched] = React.useState(false);
    if (!dispatched) {
        dispatchAction("Dashboard Viewed", { action: Action.View, resource: "Dashboard" });
        setDispatched(true);
    }
    return <></>;
};
export interface ProjectsPageLoaderData {
    configuration: DashboardConfigurationResource;
    licenseUsage: LicenseUsageResource | null;
    licenseStatus: LicenseStatusResource | null;
}
export const projectsPageLoader = async (repository: Repository, isFreeTierEnabled: boolean): Promise<ProjectsPageLoaderData> => {
    const configuration = repository.DashboardConfiguration.get();
    // Load license data if free tier is enabled
    const licenseUsageAsync = isFreeTierEnabled ? repository.Licenses.getCurrentUsage() : Promise.resolve(null);
    const licenseStatusAsync = isFreeTierEnabled ? repository.Licenses.getCurrentStatus() : Promise.resolve(null);
    return {
        configuration: await configuration,
        licenseUsage: await licenseUsageAsync,
        licenseStatus: await licenseStatusAsync,
    };
};
export function ProjectsPage({ spaceId, queryParams, setQueryParams, loaderData, projectStatus }: ProjectsPageProps) {
    const { doBusyTask, status: doBusyTaskStatus } = useLegacyDoBusyTask();
    const { errors } = useAggregateAPIOperationStatus(doBusyTaskStatus);
    const projectsFilter = useProjectsDashboardFilter(queryParams, setQueryParams);
    const [projectGroupIdForProjectDialog, setProjectGroupIdForProjectDialog] = useState<string | null>(null);
    const dispatchAction = useAnalyticActionDispatch();
    const { result: onboardingData, refetch: refetchOnboardingData } = useInlineStatusQuery((repository) => repository.UserOnboarding.getProjectsOnboardingForCurrentUser(), [], "Projects User Onboarding");
    const { isLimitReached: isProjectLimitReached, licensedLimit } = getLicenseLimit(loaderData.licenseUsage, "projects");
    const { isOpen: isAddProjectGroupDialogOpen, openDialog: openAddProjectGroupDialog, closeAllDialogs: closeAddProjectGroupDialog } = useDialogTrigger();
    const { isOpen: isAddProjectDialogOpen, openDialog: openAddProjectDialog, closeAllDialogs: closeAddProjectDialog } = useDialogTrigger();
    const isProjectTemplatesFeatureEnabled = useOctopusFeatureToggle("project-templates", false);
    const { isOpen: isProjectLimitDialogOpen, openDialog: openProjectLimitDialog, closeAllDialogs: closeProjectLimitDialog } = useDialogTrigger();
    const isFreeTierEnabled = isFeatureToggleEnabled("free-tier");
    const simpleDispatchAction = useAnalyticSimpleActionDispatch();
    const openAddProjectDialogWithoutProjectGroupId = useCallback(() => {
        setProjectGroupIdForProjectDialog(null);
        openAddProjectDialog();
    }, [openAddProjectDialog]);
    const openAddProjectDialogWithProjectGroupId = useCallback((groupId: string) => {
        setProjectGroupIdForProjectDialog(groupId);
        openAddProjectDialog();
    }, [openAddProjectDialog]);
    const { result } = useInlineStatusQuery(async (repository) => {
        const hasProjectGroupViewPermissions = isAllowed({ permission: Permission.ProjectGroupView, projectGroup: "*" });
        const projectGroupsPromise = hasProjectGroupViewPermissions ? repository.ProjectGroups.all() : Promise.resolve([]);
        const environmentsPromise = repository.spaceId ? repository.Environments.all() : Promise.resolve([]);
        const getProjectTemplates = async () => {
            if (!isProjectTemplatesFeatureEnabled) {
                return [];
            }
            const persistenceSettings = await repository.PlatformHub.getVersionControlSettings();
            if (!persistenceSettings.Url) {
                return [];
            }
            const response = await repository.ProjectTemplates.getPaginatedTemplates({
                skip: 0,
                take: 100,
                partialName: "",
                gitRef: persistenceSettings.DefaultBranch,
            });
            return response.ProjectTemplates;
        };
        const projectTemplatesPromise = getProjectTemplates();
        const [projectGroups, environments, projectTemplates] = await Promise.all([projectGroupsPromise, environmentsPromise, projectTemplatesPromise]);
        return { projectGroups, environments, projectTemplates };
    }, [isProjectTemplatesFeatureEnabled], "Project Groups and Environments");
    const projectGroups = result?.projectGroups;
    const environments = result?.environments;
    const projectTemplates = result?.projectTemplates;
    const importExportPageAction: PageAction = {
        type: "custom",
        key: "importExport",
        content: <ImportExportButton spaceId={spaceId}/>,
    };
    const addProjectGroupPageAction: PageAction = {
        type: "button",
        label: "Add Project Group",
        hasPermissions: isAllowed({ permission: Permission.ProjectGroupCreate, projectGroup: "*" }),
        buttonType: "secondary",
        onClick: () => {
            dispatchAction("Add Project Group", { resource: "Project Group", action: Action.Add });
            openAddProjectGroupDialog();
        },
    };
    const addProjectPageAction: PrimaryPageAction = {
        type: "button",
        label: "Add Project",
        hasPermissions: isAllowed({ permission: Permission.ProjectCreate, projectGroup: "*" }),
        onClick: () => {
            if (isProjectLimitReached && isFreeTierEnabled) {
                openProjectLimitDialog();
                simpleDispatchAction(EntitlementLimitPopUpEvent);
            }
            else {
                dispatchAction("Add Project", { resource: "Project", action: Action.Add });
                projectStatus.dispatch({
                    type: projectStatusActions.projectInitiated,
                    hasEnvironments: environments ? environments.length > 0 : false,
                    hasProjectTemplates: projectTemplates ? projectTemplates.length > 0 : false,
                });
                openAddProjectDialogWithoutProjectGroupId();
            }
        },
        disabled: !projectGroups || projectGroups.length === 0,
    };
    const close = (project: ProjectResource | undefined) => {
        if (project) {
            dispatchAction("Save Project", { action: Action.Save, resource: "Project" });
            refetchOnboardingData();
        }
        else {
            dispatchAction("Cancel Project Creation", { action: Action.Cancel, resource: "Project" });
        }
        closeAddProjectDialog();
    };
    const dialogs = (<>
            <Dialog open={isAddProjectGroupDialogOpen}>
                {/* After new Project Group is created in existing Projects page, it redirects to itself with the new projectGroupId as query param.
            Then the componentDidUpdate will trigger, and Project Groups will be reloaded, hence showing the newly created one.
            However, for this temporary page, how we show empty Project Group is still TBD, therefore just closing the dialog here for now.
            It is not good user experience, because user does not know whether a new Project Group is created or not, we will come back to solve this.
          */}
                <AddProjectGroup projectGroupCreated={(resource) => {
            setQueryParams({ page: 1, searchValue: resource.Name });
            closeAddProjectGroupDialog();
        }}/>
            </Dialog>
            {isAddProjectDialogOpen && (<NewProjectWizard open={isAddProjectDialogOpen} spaceId={spaceId} projectGroupId={projectGroupIdForProjectDialog ?? undefined} doBusyTask={doBusyTask} isOnboarding={false} close={close} projectStatus={projectStatus}/>)}
            <UpsellDialogWithLicenseUsage title="Need more projects?" content={<>
                        You've reached your <strong>{licensedLimit}-project</strong> limit. Upgrade to add more.
                    </>} open={isProjectLimitDialogOpen} onClose={closeProjectLimitDialog} licenseSerialNumber={loaderData.licenseStatus?.SerialNumber} licenseUsage={loaderData.licenseUsage}/>
        </>);
    if (!onboardingData) {
        return <PageLoading errors={errors}/>;
    }
    // If this is a fresh instance, show onboarding.
    if (onboardingData.TotalProjectsInSpace === 0) {
        return (<>
                <DashboardOnboardingLayout header={{ title: "Projects", actions: [importExportPageAction, addProjectGroupPageAction], primaryAction: addProjectPageAction }} spaceId={spaceId} canContinueOnboarding={false} onboardingProject={undefined} doBusyTask={doBusyTask} projectStatus={projectStatus}/>
                {dialogs}
            </>);
    }
    if (onboardingData.ContinueFirstProject && hasPermission(Permission.ProjectCreate)) {
        return (<>
                <DashboardOnboardingLayout header={{ title: "Projects", actions: [importExportPageAction, addProjectGroupPageAction], primaryAction: addProjectPageAction }} spaceId={spaceId} canContinueOnboarding={!!onboardingData.ContinueFirstProject} onboardingProject={onboardingData.ContinueFirstProject} doBusyTask={doBusyTask} projectStatus={projectStatus}/>
                {dialogs}
            </>);
    }
    return (<PermissionCheck permission={Permission.ProjectView} project="*" alternate={<NoProjectViewPermissionsDashboard />}>
            <DashboardDispatcher dispatchAction={dispatchAction}/>
            <ProjectsDashboardPageContent spaceId={spaceId} pageActions={[importExportPageAction, addProjectGroupPageAction]} primaryPageAction={addProjectPageAction} doBusyTaskStatus={doBusyTaskStatus} dashboardConfiguration={loaderData.configuration} filter={projectsFilter} setFilter={setQueryParams} onAddProjectToGroup={openAddProjectDialogWithProjectGroupId} projectStatus={projectStatus}/>
            {dialogs}
        </PermissionCheck>);
}
function useProjectsDashboardFilter(queryParams: ProjectsQueryParams, setQueryParams: QueryParamValuesSetter<ProjectsQueryParams>): ProjectsFilter {
    const [userDefaultProjectsPageSize, setUserDefaultProjectsPageSize] = useLocalStorage(`Octopus.ProjectsDashboard.PageSize`, defaultProjectsPageSize);
    const filter = useMemo(() => ({
        searchValue: queryParams.searchValue,
        page: queryParams.page ?? 1,
        pageSize: (isProjectsPageSize(queryParams.pageSize) ? queryParams.pageSize : undefined) ?? userDefaultProjectsPageSize,
        includeProjectGroupId: queryParams.includeProjectGroupId,
    }), [userDefaultProjectsPageSize, queryParams.page, queryParams.pageSize, queryParams.searchValue, queryParams.includeProjectGroupId]);
    if (userDefaultProjectsPageSize !== filter.pageSize) {
        setUserDefaultProjectsPageSize(filter.pageSize);
    }
    useEffect(() => {
        if (!isEqual(filter, queryParams))
            setQueryParams(filter);
    }, [queryParams, filter, setQueryParams]);
    return filter;
}
export function ImportExportButton({ spaceId }: {
    spaceId: string;
}) {
    const [openMenu, menuState, buttonAriaAttributes] = useMenuState();
    return (<>
            <Button importance="secondary" label="Import/Export" onClick={openMenu} {...buttonAriaAttributes}/>
            <SimpleMenu menuState={menuState} items={getImportExportMenuItems(spaceId)} accessibleName={"Import/Export Menu"} compact={true}/>
        </>);
}
function NoProjectViewPermissionsDashboard() {
    return (<Level1EmptyStateLayout title={"Permissions"} intro={"Your current permissions don\u2019t allow you to view projects. If you require more information or believe this is in error please contact your administrator. "} image={<EmptyStateDashboardNoPermissionIllustration />}/>);
}
